/**
 * WebGL renderer for the Burning Ship fractal.
 * Uses GPU parallelism for much higher throughput than CPU/workers.
 * Falls back to canvas 2D when WebGL is unavailable.
 *
 * Burning Ship: x' = x² - y² + x0, y' = 2|xy| + y0. Escape when x²+y² ≥ 4.
 * Coloring matches fractal_math.js (mu-based smooth coloring).
 */
import { FIRE_PALETTE_GLSL } from './firePalette';
import { isCanvasFullscreen } from './fullscreenControl';

const VERTEX_SHADER_SRC = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = 0.5 * (a_position + 1.0);
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER_SRC = `
  precision highp float;
  varying vec2 v_uv;
  uniform vec2 u_xRange;
  uniform vec2 u_yRange;
  uniform float u_r;
  uniform float u_gScale;
  uniform float u_b;
  uniform float u_aScale;
  uniform float u_useMu;
  uniform float u_useFixedAlpha;
  uniform float u_aFixed;
  uniform float u_useFirePalette;
  uniform float u_useValueRange;
  uniform vec2 u_valueRange;
  uniform float u_nearMinusOneGuard;
  uniform vec2 u_guardCenter;
  uniform vec2 u_guardHalfExtent;

  ${FIRE_PALETTE_GLSL}

  void main() {
    float x0 = mix(u_xRange.x, u_xRange.y, v_uv.x);
    // Flip Y to match the 2D canvas orientation (top row = yRange.x)
    float y0 = mix(u_yRange.y, u_yRange.x, v_uv.y);

    // Host-selected subsets of proved interior rectangles. The near-minus-one
    // and real-axis transient boxes leave float interpolation headroom.
    if (u_nearMinusOneGuard > 0.5) {
      if (abs(x0 - u_guardCenter.x) <= u_guardHalfExtent.x
          && abs(y0 - u_guardCenter.y) <= u_guardHalfExtent.y) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
        return;
      }
    }
    if (x0 * x0 + y0 * y0 <= 0.0624995) {
      gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
      return;
    }

    // Match JS iterateUntilEscape logic:
    // start with x = y = 0, iteration = 0
    float x = 0.0;
    float y = 0.0;
    float x2 = 0.0;
    float y2 = 0.0;
    float distSq = 0.0;
    float iterCount = 0.0;
    bool escaped = false;
    float ox = 0.0;
    float oy = 0.0;
    int checkLen = 8;
    int stepInCycle = 0;

    for (int i = 0; i < 1000; i++) {
      if (u_useValueRange < 0.5 && i >= 255) {
        break;
      }
      // JS loop condition: while (x2 + y2 < 4 && iteration < MAX_ITERATIONS)
      distSq = x2 + y2;
      if (distSq >= 4.0) {
        escaped = true;
        break;
      }

      float xy = x * y;
      float absXY = xy >= 0.0 ? xy : -xy;
      float xnew = x2 - y2 + x0;
      float ynew = 2.0 * absXY + y0;
      x = xnew;
      y = ynew;
      x2 = x * x;
      y2 = y * y;
      iterCount += 1.0;
      if (x == ox && y == oy) {
        break;
      }
      stepInCycle++;
      if (stepInCycle == checkLen) {
        ox = x;
        oy = y;
        stepInCycle = 0;
        checkLen += checkLen;
      }
    }

    // If we never escaped within MAX_ITERATIONS, leave pixel transparent
    if (!escaped) {
      if (u_useValueRange > 0.5) {
        if (iterCount >= 68.0 && abs(y0) > 0.00005) {
          gl_FragColor = vec4(warmRangeColor(u_valueRange.y, u_valueRange), 1.0);
          return;
        }
        gl_FragColor = vec4(10.0 / 255.0, 9.0 / 255.0, 14.0 / 255.0, 1.0);
        return;
      }
      gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
      return;
    }

    // normalizedIterationCount(iterCount, distSq) from JS:
    float logDist = 0.5 * log(distSq);
    float mu = iterCount + 1.0 - log(logDist) / log(2.0);

    float val = u_useMu > 0.5 ? mu : iterCount;
    if (u_useValueRange > 0.5) {
      gl_FragColor = vec4(warmRangeColor(val, u_valueRange), 1.0);
      return;
    }
    if (u_useFirePalette > 0.5) {
      gl_FragColor = vec4(fireColor(val), 1.0);
      return;
    }
    float g = clamp(val * u_gScale, 0.0, 255.0);
    float a = u_useFixedAlpha > 0.5 ? u_aFixed : clamp(val * u_aScale, 0.0, 255.0);

    gl_FragColor = vec4(u_r / 255.0, g / 255.0, u_b / 255.0, a / 255.0);
  }
`;

const QUAD = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
const WEBGL_CONTEXT_ATTRIBUTES = Object.freeze({ alpha: false, antialias: false });
const INTERIOR_RADIUS = 0.25 - 1e-6;
const PERIOD_TWO_COORDINATE_BOUND = 0.019 - 1e-6;
const HORIZONTAL_REAL_BOUND = 0.035 - 1e-6;
const HORIZONTAL_IMAGINARY_BOUND = 0.001 - 1e-6;
const NEGATIVE_REAL_LOWER_BOUND = -0.13 + 1e-6;
const NEGATIVE_REAL_UPPER_BOUND = -0.035 - 1e-6;
const FAR_NEGATIVE_REAL_LOWER_BOUND = -0.161 + 1e-6;
const FAR_NEGATIVE_REAL_UPPER_BOUND = -0.159 - 1e-6;
const NARROW_IMAGINARY_BOUND = 0.0006 - 1e-6;
const POSITIVE_REAL_LOWER_BOUND = 0.034 + 1e-6;
const POSITIVE_REAL_UPPER_BOUND = 0.101 - 1e-6;
const MID_POSITIVE_REAL_LOWER_BOUND = 0.115 + 1e-6;
const MID_POSITIVE_REAL_UPPER_BOUND = 0.125 - 1e-6;
const FAR_POSITIVE_REAL_LOWER_BOUND = 0.145 + 1e-6;
const FAR_POSITIVE_REAL_UPPER_BOUND = 0.155 - 1e-6;
const LATER_POSITIVE_REAL_LOWER_BOUND = 0.179 + 1e-6;
const LATER_POSITIVE_REAL_UPPER_BOUND = 0.181 - 1e-6;
const FARTHEST_POSITIVE_REAL_LOWER_BOUND = 0.1995 + 1e-6;
const FARTHEST_POSITIVE_REAL_UPPER_BOUND = 0.2005 - 1e-6;
const NEAR_MINUS_ONE_PIXEL_BOUND = 0.0189;
const PROVED_PIXEL_INTERIOR_GUARDS = [
  { centerX: -1, centerY: 0,
    halfX: NEAR_MINUS_ONE_PIXEL_BOUND, halfY: NEAR_MINUS_ONE_PIXEL_BOUND },
  // Origin-disk subsets with 1e-4 coordinate headroom. The numerical
  // margin is conditional; queried format precision alone is not a proof.
  { centerX: -1, centerY: 0, halfX: 0.0409, halfY: 0.0339 },
  { centerX: -1, centerY: 0, halfX: 0.0599, halfY: 0.0259 },
];
// Two-step origin-disk proofs cover both signs of the imaginary coordinate.
// For Q(z)=(Re(z²),|Im(z²)|), |Q(z)|=|z|² and Q is a reflected square.
// The proved rectangles use (C,A,r)=(1.042,.114,.2)/(1.061,.104,.18).
// Each leaves strict return margin; inset endpoints by 1e-12 for sampling.
const ORIGIN_DISK_PERIOD_TWO_RECTANGLES = [
  { xLower: -1.041 + 1e-12, xUpper: -0.959 - 1e-12,
    yBound: 0.034 - 1e-12 },
  { xLower: -1.06 + 1e-12, xUpper: -0.94 - 1e-12,
    yBound: 0.026 - 1e-12 },
];

// These three independently proved inner boxes meet across a thin real-axis
// camera band. Only a box containing the camera's *entire* Y range may
// contribute X coverage; matching different corners is insufficient.
const ORIGIN_WIDE_RECTANGLE = ORIGIN_DISK_PERIOD_TWO_RECTANGLES[1];
const PROVED_WHOLE_REAL_AXIS_UNION_RECTANGLES = [
  { xLower: -1 + NEGATIVE_REAL_LOWER_BOUND,
    xUpper: -1 + NEGATIVE_REAL_UPPER_BOUND,
    yLower: -HORIZONTAL_IMAGINARY_BOUND, yUpper: HORIZONTAL_IMAGINARY_BOUND },
  { xLower: ORIGIN_WIDE_RECTANGLE.xLower, xUpper: ORIGIN_WIDE_RECTANGLE.xUpper,
    yLower: -ORIGIN_WIDE_RECTANGLE.yBound, yUpper: ORIGIN_WIDE_RECTANGLE.yBound },
  { xLower: -1 + POSITIVE_REAL_LOWER_BOUND,
    xUpper: -1 + POSITIVE_REAL_UPPER_BOUND,
    yLower: -HORIZONTAL_IMAGINARY_BOUND, yUpper: HORIZONTAL_IMAGINARY_BOUND },
];

function isValidRangePair(xRange, yRange) {
  return Array.isArray(xRange) && Array.isArray(yRange)
    && xRange.length === 2 && yRange.length === 2
    && Number.isFinite(xRange[0]) && Number.isFinite(xRange[1])
    && Number.isFinite(yRange[0]) && Number.isFinite(yRange[1])
    && xRange[0] < xRange[1] && yRange[0] < yRange[1];
}

function isWholeProvedRectangleUnionView(xRange, yRange) {
  if (!isValidRangePair(xRange, yRange)) return false;

  let coveredX = xRange[0];
  for (let i = 0; i < PROVED_WHOLE_REAL_AXIS_UNION_RECTANGLES.length; i++) {
    const rect = PROVED_WHOLE_REAL_AXIS_UNION_RECTANGLES[i];
    if (yRange[0] < rect.yLower || yRange[1] > rect.yUpper
      || xRange[0] > rect.xUpper || xRange[1] < rect.xLower) continue;
    if (rect.xLower > coveredX) return false;
    if (rect.xUpper > coveredX) coveredX = rect.xUpper;
    if (coveredX >= xRange[1]) return true;
  }
  return false;
}

function isWholeOriginDiskPeriodTwoView(xRange, yRange) {
  if (!isValidRangePair(xRange, yRange)) return false;
  for (let i = 0; i < ORIGIN_DISK_PERIOD_TWO_RECTANGLES.length; i++) {
    const rect = ORIGIN_DISK_PERIOD_TWO_RECTANGLES[i];
    if (xRange[0] >= rect.xLower && xRange[1] <= rect.xUpper
      && yRange[0] >= -rect.yBound && yRange[1] <= rect.yBound) return true;
  }
  return false;
}

const MIN_NEAR_MINUS_ONE_COVERAGE = 0.50;

function hasSubstantialPixelGuardOverlap(xRange, yRange, guard) {
  if (!isValidRangePair(xRange, yRange)) return false;

  const xOverlap = Math.max(0, Math.min(xRange[1], guard.centerX + guard.halfX)
    - Math.max(xRange[0], guard.centerX - guard.halfX));
  const yOverlap = Math.max(0, Math.min(yRange[1], guard.centerY + guard.halfY)
    - Math.max(yRange[0], guard.centerY - guard.halfY));
  const xSpan = xRange[1] - xRange[0];
  const ySpan = yRange[1] - yRange[0];
  const viewArea = xSpan * ySpan;
  return Number.isFinite(viewArea) && viewArea > 0
    && xOverlap > 0 && yOverlap > 0
    && (xOverlap / xSpan) * (yOverlap / ySpan) >= MIN_NEAR_MINUS_ONE_COVERAGE;
}

/** Select the first eligible proved rectangle with substantial camera overlap. */
export function pixelInteriorGuardForWebGLView(xRange, yRange,
  allowTransient = true) {
  for (let index = 0; index < PROVED_PIXEL_INTERIOR_GUARDS.length; index++) {
    const guard = PROVED_PIXEL_INTERIOR_GUARDS[index];
    if ((index !== 0 && !allowTransient)
      || !hasSubstantialPixelGuardOverlap(xRange, yRange, guard)) continue;
    return guard;
  }
  return null;
}

const REAL_AXIS_PERIOD_TWO_BANDS = [
  [NEGATIVE_REAL_LOWER_BOUND, NEGATIVE_REAL_UPPER_BOUND, HORIZONTAL_IMAGINARY_BOUND],
  [FAR_NEGATIVE_REAL_LOWER_BOUND, FAR_NEGATIVE_REAL_UPPER_BOUND, NARROW_IMAGINARY_BOUND],
  [POSITIVE_REAL_LOWER_BOUND, POSITIVE_REAL_UPPER_BOUND, HORIZONTAL_IMAGINARY_BOUND],
  [MID_POSITIVE_REAL_LOWER_BOUND, MID_POSITIVE_REAL_UPPER_BOUND, HORIZONTAL_IMAGINARY_BOUND],
  [FAR_POSITIVE_REAL_LOWER_BOUND, FAR_POSITIVE_REAL_UPPER_BOUND, HORIZONTAL_IMAGINARY_BOUND],
  [LATER_POSITIVE_REAL_LOWER_BOUND, LATER_POSITIVE_REAL_UPPER_BOUND, NARROW_IMAGINARY_BOUND],
  [FARTHEST_POSITIVE_REAL_LOWER_BOUND, FARTHEST_POSITIVE_REAL_UPPER_BOUND, NARROW_IMAGINARY_BOUND],
];

/** A whole view inside this disk has no escaping Burning Ship samples. */
export function isWholeWebGLViewInterior(xRange, yRange, allowTransient = true) {
  if (!isValidRangePair(xRange, yRange)) return false;

  const maxX = Math.max(Math.abs(xRange[0]), Math.abs(xRange[1]));
  const maxY = Math.max(Math.abs(yRange[0]), Math.abs(yRange[1]));
  if (Math.hypot(maxX, maxY) <= INTERIOR_RADIUS) return true;

  const maxRealOffset = Math.max(Math.abs(xRange[0] + 1), Math.abs(xRange[1] + 1));
  if (maxRealOffset <= PERIOD_TWO_COORDINATE_BOUND
    && maxY <= PERIOD_TWO_COORDINATE_BOUND) return true;
  if (maxRealOffset <= HORIZONTAL_REAL_BOUND
    && maxY <= HORIZONTAL_IMAGINARY_BOUND) return true;

  const realLower = xRange[0] + 1;
  const realUpper = xRange[1] + 1;
  for (const [lower, upper, yBound] of REAL_AXIS_PERIOD_TWO_BANDS) {
    if (realLower >= lower && realUpper <= upper && maxY <= yBound) return true;
  }

  // Host-side transient proofs use the cached highp precision gate in draw.
  return Boolean(allowTransient && (isWholeProvedRectangleUnionView(xRange, yRange)
    || isWholeOriginDiskPeriodTwoView(xRange, yRange)));
}

function compileShader(gl, type, src) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile: ${log}`);
  }
  return shader;
}

function createProgram(gl, vsSrc, fsSrc) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link: ${log}`);
  }
  return program;
}

/**
 * Try to get a WebGL context. Prefer WebGL2, fall back to WebGL1.
 * Returns { gl, isWebGL2 } or null if not supported.
 */
export function getWebGLContext(canvas) {
  if (!canvas) return null;
  let gl = typeof WebGL2RenderingContext === 'undefined' ? null
    : canvas.getContext('webgl2', WEBGL_CONTEXT_ATTRIBUTES);
  const isWebGL2 = !!gl;
  if (!gl) {
    gl = canvas.getContext('webgl', WEBGL_CONTEXT_ATTRIBUTES);
  }
  if (!gl || gl.isContextLost?.()) {
    canvas._fractalWebGLResources = null;
    return null;
  }
  return { gl, isWebGL2 };
}

function supportsTransientPixelPrecision(gl, canvasEl) {
  const cached = canvasEl._fractalWebGLPrecision;
  if (cached?.gl === gl) return cached.supported;

  // WebGL1 highp can legally have only 16 bits of relative precision. At
  // c≈-1.22, that may exceed the margins of the narrow per-pixel subsets.
  let supported = false;
  if (typeof gl.getShaderPrecisionFormat === 'function') {
    try {
      const vertex = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT);
      const fragment = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
      supported = vertex?.precision >= 23 && fragment?.precision >= 23;
    } catch { /* Fail closed when the precision query is unavailable. */ }
  }
  canvasEl._fractalWebGLPrecision = { gl, supported };
  return supported;
}

function getFractalResources(gl, canvasEl) {
  const cached = canvasEl._fractalWebGLResources;
  if (cached && cached.gl === gl) return cached;

  const program = createProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC);
  const buffer = gl.createBuffer();
  if (!buffer) {
    gl.deleteProgram(program);
    throw new Error('Could not create fullscreen quad buffer');
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD, gl.STATIC_DRAW);

  const uniforms = {
    xRange: gl.getUniformLocation(program, 'u_xRange'),
    yRange: gl.getUniformLocation(program, 'u_yRange'),
    r: gl.getUniformLocation(program, 'u_r'),
    gScale: gl.getUniformLocation(program, 'u_gScale'),
    b: gl.getUniformLocation(program, 'u_b'),
    aScale: gl.getUniformLocation(program, 'u_aScale'),
    useMu: gl.getUniformLocation(program, 'u_useMu'),
    useFirePalette: gl.getUniformLocation(program, 'u_useFirePalette'),
    useValueRange: gl.getUniformLocation(program, 'u_useValueRange'),
    valueRange: gl.getUniformLocation(program, 'u_valueRange'),
    nearMinusOneGuard: gl.getUniformLocation(program, 'u_nearMinusOneGuard'),
    guardCenter: gl.getUniformLocation(program, 'u_guardCenter'),
    guardHalfExtent: gl.getUniformLocation(program, 'u_guardHalfExtent'),
    useFixedAlpha: gl.getUniformLocation(program, 'u_useFixedAlpha'),
    aFixed: gl.getUniformLocation(program, 'u_aFixed'),
  };

  const supportsTransientPixelGuard = supportsTransientPixelPrecision(gl, canvasEl);

  const resources = {
    gl,
    program,
    buffer,
    position: gl.getAttribLocation(program, 'a_position'),
    uniforms,
    supportsTransientPixelGuard,
  };
  canvasEl._fractalWebGLResources = resources;
  return resources;
}

export const MAX_ACTIVE_WEBGL_CONTEXTS = 6;
const MAX_OFFLOAD_SNAPSHOT_SIDE = 320;
const activeWebGLCanvases = new Map();
let webglVisibilityObserver = null;
let webglVisibilityObserverCtor = null;

function isCanvasNearViewport(canvas) {
  if (typeof window === 'undefined' || !canvas?.isConnected
    || typeof canvas.getBoundingClientRect !== 'function') return false;
  if (isCanvasFullscreen(canvas)) return true;
  const rect = canvas.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement?.clientHeight || 800;
  const vw = window.innerWidth || document.documentElement?.clientWidth || 1200;
  const right = rect.right ?? (rect.left + rect.width);
  const bottom = rect.bottom ?? (rect.top + rect.height);
  return rect.width > 0 && rect.height > 0
    && bottom >= -200 && rect.top <= vh + 200
    && right >= -200 && rect.left <= vw + 200;
}

function observeWebGLCanvasVisibility(canvasEl) {
  if (typeof IntersectionObserver === 'undefined' || globalThis.IntersectionObserver?.mock
    || !canvasEl?.isConnected) return;
  if (webglVisibilityObserverCtor !== IntersectionObserver || !webglVisibilityObserver) {
    webglVisibilityObserver?.disconnect?.();
    webglVisibilityObserverCtor = IntersectionObserver;
    webglVisibilityObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const target = entry.target;
        if (!entry.isIntersecting && target && activeWebGLCanvases.has(target)
          && activeWebGLCanvases.size > 3
          && !isCanvasNearViewport(target)) {
          offloadWebGLCanvas(target);
        }
      }
    }, { rootMargin: '1200px 0px' });
  }
  webglVisibilityObserver.observe(canvasEl);
}

function unobserveWebGLCanvasVisibility(canvasEl) {
  if (!webglVisibilityObserver || !canvasEl) return;
  webglVisibilityObserver.unobserve?.(canvasEl);
  if (activeWebGLCanvases.size === 0) {
    webglVisibilityObserver.disconnect?.();
    webglVisibilityObserver = null;
  }
}

export function disposeWebGLResources(canvasEl) {
  if (!canvasEl) return;
  activeWebGLCanvases.delete(canvasEl);
  unobserveWebGLCanvasVisibility(canvasEl);
  const resources = canvasEl._fractalWebGLResources;
  canvasEl._fractalWebGLResources = null;
  canvasEl._fractalWebGLPrecision = null;
  if (!resources?.gl) return;
  const { gl, program, buffer } = resources;
  try {
    if (!gl.isContextLost?.()) {
      if (buffer) gl.deleteBuffer?.(buffer);
      if (program) gl.deleteProgram?.(program);
    }
  } catch { /* Ignore teardown errors on a lost or disposed context. */ }
}

function renderWebGLScene(canvasEl, gl, xRange, yRange, colorParams) {
  const w = canvasEl.width;
  const h = canvasEl.height;
  if (w <= 0 || h <= 0) return false;

  const provedWholeView = isWholeWebGLViewInterior(xRange, yRange, false);
  const provedTransientView = !provedWholeView
    && (isWholeProvedRectangleUnionView(xRange, yRange)
      || isWholeOriginDiskPeriodTwoView(xRange, yRange))
    && supportsTransientPixelPrecision(gl, canvasEl);
  if (provedWholeView || provedTransientView) {
    clearTransparentTarget(gl, null, w, h);
    return true;
  }

  let resources;
  try {
    resources = getFractalResources(gl, canvasEl);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('WebGL program creation failed:', err.message);
    }
    return false;
  }

  const cp = colorParams || {};
  const pixelGuard = pixelInteriorGuardForWebGLView(xRange, yRange,
    resources.supportsTransientPixelGuard);
  drawFractal(gl, null, w, h, resources, xRange, yRange, cp, pixelGuard);
  return true;
}

function offloadWebGLCanvas(canvasEl) {
  if (!canvasEl) return false;
  const entry = activeWebGLCanvases.get(canvasEl);
  activeWebGLCanvases.delete(canvasEl);
  const gl = entry?.gl || canvasEl._fractalWebGLResources?.gl || canvasEl._fractalWebGLPrecision?.gl;
  if (typeof document !== 'undefined' && canvasEl.isConnected
    && canvasEl.width > 0 && canvasEl.height > 0 && !gl?.isContextLost?.()) {
    try {
      const isDomCanvas = typeof HTMLCanvasElement === 'undefined'
        || canvasEl instanceof HTMLCanvasElement;
      const scale = Math.min(
        1,
        MAX_OFFLOAD_SNAPSHOT_SIDE / Math.max(canvasEl.width, canvasEl.height, 1),
      );
      const snapWidth = Math.max(1, Math.round(canvasEl.width * scale));
      const snapHeight = Math.max(1, Math.round(canvasEl.height * scale));
      if (isDomCanvas && entry?.xRange && entry?.yRange
        && (canvasEl.width !== snapWidth || canvasEl.height !== snapHeight)) {
        canvasEl.width = snapWidth;
        canvasEl.height = snapHeight;
      }
      // Default WebGL contexts (preserveDrawingBuffer: false) discard their
      // drawing buffer after compositing. Repaint synchronously before
      // drawImage so the 2D snapshot captures the rendered fractal rather
      // than a cleared black buffer.
      if (entry?.xRange && entry?.yRange) {
        renderWebGLScene(canvasEl, gl, entry.xRange, entry.yRange, entry.colorParams);
      }
      if (isDomCanvas) {
        const snapshot = document.createElement('canvas');
        snapshot.width = snapWidth;
        snapshot.height = snapHeight;
        const ctx2d = snapshot.getContext('2d');
        if (ctx2d) {
          ctx2d.imageSmoothingEnabled = true;
          ctx2d.drawImage(canvasEl, 0, 0, snapWidth, snapHeight);
          if (canvasEl._offloadedSnapshot && canvasEl._offloadedSnapshot !== snapshot) {
            canvasEl._offloadedSnapshot.width = 0;
            canvasEl._offloadedSnapshot.height = 0;
          }
          canvasEl._offloadedSnapshot = snapshot;
        }
      }
    } catch { /* Ignore snapshot copy errors. */ }
  }
  disposeWebGLResources(canvasEl);
  if (gl && !gl.isContextLost?.()) {
    try {
      const loseExt = gl.getExtension?.('WEBGL_lose_context');
      if (loseExt?.loseContext) {
        canvasEl._webGLOffloaded = true;
        loseExt.loseContext();
        return true;
      }
    } catch { /* Ignore extension errors. */ }
  }
  if (canvasEl._offloadedSnapshot) {
    canvasEl._offloadedSnapshot.width = 0;
    canvasEl._offloadedSnapshot.height = 0;
    canvasEl._offloadedSnapshot = null;
  }
  return false;
}

export function offloadFarWebGLCanvases() {
  for (const [candidate, record] of activeWebGLCanvases) {
    if (candidate.isConnected === false || record.gl?.isContextLost?.()) {
      disposeWebGLResources(candidate);
    } else if (activeWebGLCanvases.size > 3 && !isCanvasNearViewport(candidate)) {
      offloadWebGLCanvas(candidate);
    }
  }
}

function trackActiveWebGLCanvas(canvasEl, gl, xRange, yRange, colorParams) {
  if (!canvasEl || (!canvasEl.isConnected && typeof gl?.getExtension !== 'function')) return;
  const existing = activeWebGLCanvases.get(canvasEl);
  if (existing) {
    activeWebGLCanvases.delete(canvasEl);
    existing.gl = gl;
    if (Array.isArray(xRange)) {
      if (existing.xRange) {
        existing.xRange[0] = xRange[0];
        existing.xRange[1] = xRange[1];
      } else {
        existing.xRange = [xRange[0], xRange[1]];
      }
    } else {
      existing.xRange = null;
    }
    if (Array.isArray(yRange)) {
      if (existing.yRange) {
        existing.yRange[0] = yRange[0];
        existing.yRange[1] = yRange[1];
      } else {
        existing.yRange = [yRange[0], yRange[1]];
      }
    } else {
      existing.yRange = null;
    }
    existing.colorParams = colorParams;
    activeWebGLCanvases.set(canvasEl, existing);
  } else {
    activeWebGLCanvases.set(canvasEl, {
      gl,
      xRange: Array.isArray(xRange) ? [xRange[0], xRange[1]] : null,
      yRange: Array.isArray(yRange) ? [yRange[0], yRange[1]] : null,
      colorParams,
    });
    observeWebGLCanvasVisibility(canvasEl);
  }
  if (activeWebGLCanvases.size <= MAX_ACTIVE_WEBGL_CONTEXTS) return;
  for (const [candidate, record] of activeWebGLCanvases) {
    if (candidate === canvasEl) continue;
    if (candidate.isConnected === false || record.gl?.isContextLost?.()) {
      disposeWebGLResources(candidate);
      continue;
    }
    if (!isCanvasNearViewport(candidate)) {
      offloadWebGLCanvas(candidate);
      if (activeWebGLCanvases.size <= MAX_ACTIVE_WEBGL_CONTEXTS) return;
    }
  }
  while (activeWebGLCanvases.size > MAX_ACTIVE_WEBGL_CONTEXTS) {
    const [oldest] = activeWebGLCanvases.keys();
    if (!oldest || oldest === canvasEl || isCanvasNearViewport(oldest)) break;
    if (!offloadWebGLCanvas(oldest)) {
      activeWebGLCanvases.delete(oldest);
      break;
    }
  }
}

function clearTransparentTarget(gl, target, width, height) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, target);
  gl.viewport(0, 0, width, height);
  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
}

function bindQuad(gl, buffer, position) {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
}

function drawFractal(gl, target, width, height, resources, xRange, yRange, cp,
  pixelGuard = null) {
  const u = resources.uniforms;
  const r = cp.r !== undefined ? cp.r : 255;
  const gScale = cp.gScale !== undefined ? cp.gScale : 7;
  const b = cp.b !== undefined ? cp.b : 0;
  const aScale = cp.aScale !== undefined ? cp.aScale : 15;
  const useMu = cp.useMu !== undefined ? cp.useMu : true;
  const useFixedAlpha = cp.aFixed != null && cp.aFixed !== undefined;
  const range = cp.valueRange;
  const hasValueRange = Array.isArray(range) && range.length === 2
    && Number.isFinite(range[0]) && Number.isFinite(range[1]) && range[0] < range[1];

  clearTransparentTarget(gl, target, width, height);
  gl.useProgram(resources.program);

  gl.uniform2f(u.xRange, xRange[0], xRange[1]);
  gl.uniform2f(u.yRange, yRange[0], yRange[1]);
  gl.uniform1f(u.r, r);
  gl.uniform1f(u.gScale, gScale);
  gl.uniform1f(u.b, b);
  gl.uniform1f(u.aScale, aScale);
  gl.uniform1f(u.useMu, useMu ? 1 : 0);
  gl.uniform1f(u.useFirePalette, cp.palette === 'fire' ? 1 : 0);
  gl.uniform1f(u.useValueRange, hasValueRange ? 1 : 0);
  if (hasValueRange) {
    gl.uniform2f(u.valueRange, range[0], range[1]);
  }
  gl.uniform1f(u.nearMinusOneGuard, pixelGuard ? 1 : 0);
  if (pixelGuard) {
    gl.uniform2f(u.guardCenter, pixelGuard.centerX, pixelGuard.centerY);
    gl.uniform2f(u.guardHalfExtent, pixelGuard.halfX, pixelGuard.halfY);
  }
  gl.uniform1f(u.useFixedAlpha, useFixedAlpha ? 1 : 0);
  gl.uniform1f(u.aFixed, useFixedAlpha ? cp.aFixed : 0);

  bindQuad(gl, resources.buffer, resources.position);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

/**
 * Draw the Burning Ship fractal with WebGL.
 * colorParams: { palette, r, gScale, b, aScale, aFixed, useMu } (same as worker).
 * Returns true if drawn, false if WebGL failed (caller should fall back to canvas).
 */
export function drawBurningShipFractalWebGL(canvasEl, xRange, yRange, colorParams) {
  if (colorParams?.mode === 'nebulabrot') return false;
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;

  const { gl } = ctx;
  if (!renderWebGLScene(canvasEl, gl, xRange, yRange, colorParams)) return false;
  trackActiveWebGLCanvas(canvasEl, gl, xRange, yRange, colorParams);
  return true;
}
